import React from 'react';
import { Link, Unlink } from 'lucide-react';

export const RatesSection = ({
  // Tax settings
  taxScope,
  setTaxScope,
  gstApplicable,
  setGstApplicable,
  gstInputStyle,
  setGstInputStyle,
  gstPercent,
  
  // Rate mode
  rateMode,
  setRateMode,
  
  // Base rate and purity rates
  base24kRate,
  handleBase24kChange,
  purityRates,
  handlePurityRateChange,
  purityRateLinks,
  togglePurityLink,
  
  // Per-item rates
  items,
  itemRates,
  setItemRates,
  
  // Rate policy
  ratePolicy,
  setRatePolicy,
  lockDate,
  setLockDate
}) => {
  
  // Get unique purities from items
  const getUniquePurities = () => {
    const purities = new Set();
    items.forEach(item => {
      if (item.purity) purities.add(item.purity);
    });
    return Array.from(purities).sort().reverse(); // 22K before 18K
  };

  const uniquePurities = getUniquePurities();

  // Format lock summary based on tax scope and input style
  const getLockSummary = () => {
    if (!lockDate || !base24kRate) return null;
    
    const formattedDate = new Date(lockDate).toLocaleDateString('en-IN', { 
      day: 'numeric',
      month: 'short', 
      year: 'numeric' 
    });
    
    const formattedRate = parseFloat(base24kRate).toLocaleString('en-IN');
    
    if (taxScope === 'KACCHA') {
      return `Locked :- 24K rate:₹${formattedRate} on ${formattedDate}`;
    } else if (taxScope === 'TAXABLE') {
      if (gstInputStyle === 'EXCL') {
        return `Locked :- 24K rate:₹${formattedRate} + ${gstPercent}% GST on ${formattedDate}`;
      } else {
        return `Locked :- 24K rate:₹${formattedRate} incl. GST on ${formattedDate}`;
      }
    }
    return null;
  };

  // Determine what to show based on rate policy
  const showTaxAndRateInputs = ratePolicy === 'lock-today' || ratePolicy === 'rate-at-delivery';
  const showRateInputs = ratePolicy === 'lock-today';

  return (
    <div className="p-3 sm:p-4 space-y-3 sm:space-y-4">
      {/* Rate Policy - Segmented Pill Group */}
      <div>
        <label className="block text-xs font-semibold text-gray-700 mb-2">Rate Policy</label>
        <div className="flex gap-1 bg-gray-100 p-1 rounded-xl">
          <button
            type="button"
            onClick={() => {
              setRatePolicy('lock-today');
              setLockDate(new Date().toISOString().split('T')[0]);
            }}
            className={`flex-1 py-2.5 px-2 rounded-lg text-xs font-semibold transition-all min-h-[44px] ${
              ratePolicy === 'lock-today'
                ? 'bg-gradient-to-r from-amber-400 to-orange-400 text-white shadow-md'
                : 'bg-transparent text-gray-600 hover:bg-gray-200'
            }`}
          >
            Today's Rate
          </button>
          <button
            type="button"
            onClick={() => {
              setRatePolicy('rate-at-delivery');
              setLockDate('');
            }}
            className={`flex-1 py-2.5 px-2 rounded-lg text-xs font-semibold transition-all min-h-[44px] ${
              ratePolicy === 'rate-at-delivery'
                ? 'bg-gradient-to-r from-amber-400 to-orange-400 text-white shadow-md'
                : 'bg-transparent text-gray-600 hover:bg-gray-200'
            }`}
          >
            Rate On Delivery
          </button>
          <button
            type="button"
            onClick={() => {
              setRatePolicy('none');
              setLockDate('');
            }}
            className={`flex-1 py-2.5 px-2 rounded-lg text-xs font-semibold transition-all min-h-[44px] ${
              ratePolicy === 'none'
                ? 'bg-gradient-to-r from-amber-400 to-orange-400 text-white shadow-md'
                : 'bg-transparent text-gray-600 hover:bg-gray-200'
            }`}
          >
            None
          </button>
        </div>
      </div>

      {/* Tax Scope & Input Style - Show for Lock Today and Rate at Delivery */}
      {showTaxAndRateInputs && (
        <>
          {/* Tax Scope Selector - Taxable first */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-2">Tax Scope</label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  setTaxScope('TAXABLE');
                  setGstApplicable(true);
                }}
                className={`flex-1 py-2 px-3 rounded-lg text-xs sm:text-sm font-semibold transition-all ${
                  taxScope === 'TAXABLE'
                    ? 'bg-gradient-to-r from-amber-400 to-orange-400 text-white'
                    : 'bg-gray-100 text-gray-600'
                }`}
              >
                Taxable (+GST)
              </button>
              <button
                type="button"
                onClick={() => {
                  setTaxScope('KACCHA');
                  setGstApplicable(false);
                }}
                className={`flex-1 py-2 px-3 rounded-lg text-xs sm:text-sm font-semibold transition-all ${
                  taxScope === 'KACCHA'
                    ? 'bg-gradient-to-r from-amber-400 to-orange-400 text-white'
                    : 'bg-gray-100 text-gray-600'
                }`}
              >
                Kaccha (No GST)
              </button>
            </div>
            {taxScope === 'KACCHA' && (
              <div className="mt-2 p-2 bg-amber-50 rounded text-xs text-amber-800">
                <strong>Kaccha:</strong> No GST applicable. Rates entered are final base rates.
              </div>
            )}
          </div>

          {/* GST Input Style - Only for Taxable */}
          {taxScope === 'TAXABLE' && (
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-2">Input Style</label>
              <div className="space-y-2">
                <label className="flex items-start gap-2 cursor-pointer p-2 rounded hover:bg-amber-50 transition-colors">
                  <input
                    type="radio"
                    name="gstInputStyle"
                    value="EXCL"
                    checked={gstInputStyle === 'EXCL'}
                    onChange={(e) => setGstInputStyle(e.target.value)}
                    className="text-amber-500 mt-0.5 flex-shrink-0"
                  />
                  <span className="text-sm flex-1">
                    <strong>Excluding GST</strong> <span className="text-xs text-gray-600 block sm:inline">(+{gstPercent}% extra)</span>
                  </span>
                </label>
                <label className="flex items-start gap-2 cursor-pointer p-2 rounded hover:bg-amber-50 transition-colors">
                  <input
                    type="radio"
                    name="gstInputStyle"
                    value="INCL"
                    checked={gstInputStyle === 'INCL'}
                    onChange={(e) => setGstInputStyle(e.target.value)}
                    className="text-amber-500 mt-0.5 flex-shrink-0"
                  />
                  <span className="text-sm flex-1">
                    <strong>Including GST</strong> <span className="text-xs text-gray-600 block sm:inline">(GST included in rate)</span>
                  </span>
                </label>
              </div>
            </div>
          )}
        </>
      )}

      {/* Rate Inputs - Only show for Lock Today */}
      {showRateInputs && (
        <>
          {/* Rate Mode Toggle */}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setRateMode('single')}
              className={`flex-1 py-2 px-3 rounded-lg text-xs sm:text-sm font-semibold transition-all ${
                rateMode === 'single'
                  ? 'bg-gradient-to-r from-amber-400 to-orange-400 text-white'
                  : 'bg-gray-100 text-gray-600'
              }`}
            >
              Single Rate
            </button>
            <button
              type="button"
              onClick={() => setRateMode('per-item')}
              className={`flex-1 py-2 px-3 rounded-lg text-xs sm:text-sm font-semibold transition-all ${
                rateMode === 'per-item'
                  ? 'bg-gradient-to-r from-amber-400 to-orange-400 text-white'
                  : 'bg-gray-100 text-gray-600'
              }`}
            >
              Per Item
            </button>
          </div>

          {/* Single Rate Tab */}
          {rateMode === 'single' && (
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Base 24K Rate (₹/g) {gstInputStyle === 'EXCL' && taxScope === 'TAXABLE' ? '(Excl. GST)' : ''}
                </label>
                <input
                  type="number"
                  step="0.01"
                  className="input-field w-full text-sm"
                  value={base24kRate}
                  onChange={(e) => handleBase24kChange(e.target.value)}
                  placeholder="Enter base 24K rate"
                />
              </div>

              {/* Auto-computed Purity Rates - Mobile responsive */}
              {base24kRate && uniquePurities.length > 0 && (
                <div className="space-y-2">
                  <label className="block text-xs font-semibold text-gray-700">Purity Rates (₹/g)</label>
                  <div className="space-y-2">
                    {uniquePurities.map(purity => (
                      <div key={purity} className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
                        <span className="text-xs font-semibold text-gray-700 w-full sm:w-12">{purity}:</span>
                        <div className="flex items-center gap-2 w-full">
                          <input
                            type="number"
                            step="0.01"
                            className="input-field flex-1 text-sm min-w-0"
                            value={purityRates[purity] || ''}
                            onChange={(e) => handlePurityRateChange(purity, e.target.value)}
                            placeholder={`Auto-computed`}
                          />
                          <button
                            type="button"
                            onClick={() => togglePurityLink(purity)}
                            className={`p-2 rounded flex-shrink-0 ${
                              purityRateLinks[purity]
                                ? 'bg-amber-100 text-amber-700'
                                : 'bg-gray-100 text-gray-500'
                            }`}
                            title={purityRateLinks[purity] ? 'Linked to base' : 'Unlinked - manual'}
                          >
                            {purityRateLinks[purity] ? <Link className="w-4 h-4" /> : <Unlink className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 italic">
                    Click 🔗 to relink rate to base 24K, or unlink to edit manually.
                  </p>
                </div>
              )}

              {/* Lock Summary Display */}
              {ratePolicy === 'lock-today' && lockDate && base24kRate && getLockSummary() && (
                <div className="mt-3 p-3 bg-green-50 border-2 border-green-200 rounded-lg">
                  <p className="text-xs sm:text-sm text-green-800 font-semibold break-words">
                    {getLockSummary()}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Per Item Tab */}
          {rateMode === 'per-item' && (
            <div className="space-y-2">
              <label className="block text-xs font-semibold text-gray-700 mb-1">Item Rates (24K Gold)</label>
              {items.length === 0 ? (
                <p className="text-xs text-gray-500 italic p-3 bg-gray-50 rounded">Add items first to set individual rates</p>
              ) : (
                <div className="space-y-2">
                  {items.map((item, idx) => {
                    // Handle both orders (item_type) and bookings (booking_type)
                    const itemName = item.item_type || item.booking_type || 'Item';
                    
                    // Handle weight display for both Orders and Bookings
                    let weight = '';
                    if (item.weight_type === 'Fixed') {
                      // Orders with fixed weight
                      const weightFixed = parseFloat(item.weight_fixed || 0);
                      weight = weightFixed > 0 ? `${weightFixed}g` : '0g';
                    } else if (item.weight_type === 'Range') {
                      // Orders with weight range
                      const weightMin = parseFloat(item.weight_min || 0);
                      const weightMax = parseFloat(item.weight_max || 0);
                      weight = `${weightMin}-${weightMax}g`;
                    } else if (item.weight) {
                      // Bookings - single weight field
                      const bookingWeight = parseFloat(item.weight);
                      weight = bookingWeight > 0 ? `${bookingWeight}g` : '0g';
                    } else {
                      weight = '0g';
                    }
                    
                    const purity = item.purity || '24K'; // Bookings default to 24K
                    const qty = item.quantity || 1;
                    
                    return (
                      <div key={item.id} className="border border-amber-200 rounded-lg p-2 space-y-2">
                        <div className="text-xs break-words">
                          <span className="font-semibold">#{idx + 1}</span> • 
                          <span className="font-medium"> {itemName}</span> • 
                          <span className="text-gray-600">{weight}</span>
                          {item.purity && (
                            <>
                              {' • '}
                              <span className="text-gray-600">{purity}</span>
                            </>
                          )}
                          {item.quantity && (
                            <>
                              {' • '}
                              <span className="text-gray-600">Qty {qty}</span>
                            </>
                          )}
                        </div>
                        <input
                          type="number"
                          step="0.01"
                          className="input-field w-full text-sm"
                          value={itemRates[item.id] || ''}
                          onChange={(e) => setItemRates({ ...itemRates, [item.id]: e.target.value })}
                          placeholder={`24K Gold Rate (₹/g)`}
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

